import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import React, { useMemo } from 'react';
import { isDevEnvironment, mergeClassNames } from '../../../utils';

import Image from '../Image';
import { Link } from 'gatsby';
import LinkButton from '../LinkButton';
import { renderRichText } from 'gatsby-source-contentful/rich-text';
import styles from './index.module.scss';
import translateEntryLink from '../../../utils/translateEntryLink';

export default function RichText({ children, className = '', imageClassName = '', linkIcons = undefined }) {
  switch (true) {
    case children && typeof children === 'object' && 'raw' in children:
      return (
        <CMSRichText className={className} imageClassName={imageClassName} linkIcons={linkIcons}>
          {children}
        </CMSRichText>
      );
    case children && typeof children === 'object': {
      let extractedString = Object.values(children).find(child => typeof child === 'string');
      return <NotRichText className={className}>{extractedString}</NotRichText>;
    }
    case !children: {
      return null;
    }
    default: {
      return <NotRichText className={className}>{children}</NotRichText>;
    }
  }
}

function NotRichText({ children, className }) {
  return <p className={className}>{children}</p>;
}

function CMSRichText({ children, linkIcons = false, className, imageClassName }) {
  let options = useMemo(
    () => ({
      renderText: text => text.split('\n').flatMap((text, i) => [i > 0 && <br key={'br_' + i} />, text]),
      renderNode: {
        [BLOCKS.PARAGRAPH]: (node, children) => <p>{children}</p>,
        [INLINES.ENTRY_HYPERLINK]: (node, linkIcons) => renderEntryHyperlink(node, linkIcons, true),
        [INLINES.HYPERLINK]: node => renderHyperlink(node, linkIcons),
        [INLINES.ASSET_HYPERLINK]: node => renderAssetHyperlink(node, linkIcons),
        [BLOCKS.EMBEDDED_ASSET]: node => renderEmbeddedAsset(node, imageClassName),
        [BLOCKS.EMBEDDED_ENTRY]: renderEmbeddedEntry,
      },
    }),
    [linkIcons, imageClassName]
  );

  return (
    <div className={mergeClassNames('pwp', styles.richtext, className)}>
      {children && renderRichText(children, options)}
    </div>
  );
}

function renderHyperlink(node, linkIcons) {
  let [content] = node.content;
  let { uri } = node.data;
  return (
    <a className={linkIcons ? styles.urlLink : null} href={uri} target="_blank" rel="noopener noreferrer">
      {content.value}
    </a>
  );
}

function renderAssetHyperlink(node, linkIcons) {
  let [content] = node.content;
  let { target } = node.data;
  let url = target.file?.url;

  return (
    <a
      className={linkIcons ? styles.assetLink : null}
      href={`https://${url}`}
      target="_blank"
      rel="noopener noreferrer"
    >
      {content.value}
    </a>
  );
}

function renderEntryHyperlink(node, linkIcons, dfn) {
  let [content] = node.content;
  let { target } = node.data;

  if (!target?.__typename) {
    if (isDevEnvironment)
      console.error(
        `this node (entry hyperlink) is missing a graph query for it's type inside the rich text field it is being referenced.`,
        node
      );
    return null;
  }

  switch (target.__typename) {
    case 'ContentfulRichTextDefinition':
      if (dfn) {
        return <dfn title={target.description}>{content.value}</dfn>;
      }
      return null;
    default:
      let link = translateEntryLink(target);
      return (
        <Link className={linkIcons ? styles.entryLink : null} to={link}>
          {content.value}
        </Link>
      );
  }
}

function renderEmbeddedEntry(node) {
  let { __typename, ...target } = node.data.target;

  switch (__typename) {
    case 'ContentfulButton':
      return <LinkButton {...target} />;
    case 'ContentfulRichTextVideo': {
      let { url } = target;
      return (
        <div className={styles.videoWrapper}>
          <iframe
            title={url.title}
            width={560}
            height={315}
            src={url}
            frameBorder={0}
            allow="autoplay; encrypted-media"
            allowFullScreen
          />
        </div>
      );
    }
    default:
      let warning = `Unsopported embed type ${__typename}`;
      console.warn(warning);
      return isDevEnvironment ? <p>{warning}</p> : null;
  }
}

function renderEmbeddedAsset(node, imageClassName) {
  let { file, fluid, description = {} } = node.data.target;
  let { contentType } = file || {};

  switch (contentType) {
    case 'image/jpeg':
    case 'image/png':
    case 'image/webp': {
      return <Image className={imageClassName} fluid={fluid} alt={description} />;
    }
    case 'image/svg+xml':
      return <img className={mergeClassNames(styles.inlineSVG, imageClassName)} src={file.url} alt={description} />;
    default:
      return isDevEnvironment ? <p>Unsopported asset type '{contentType}'</p> : null;
  }
}

const escapeList = {
  [BLOCKS.HEADING_1]: (node, children) => <>{children}</>,
  [BLOCKS.HEADING_2]: (node, children) => <>{children}</>,
  [BLOCKS.HEADING_3]: (node, children) => <>{children}</>,
  [BLOCKS.HEADING_4]: (node, children) => <>{children}</>,
  [BLOCKS.HEADING_5]: (node, children) => <>{children}</>,
  [BLOCKS.HEADING_6]: (node, children) => <>{children}</>,
  [BLOCKS.OL_LIST]: (node, children) => <>{children}</>,
  [BLOCKS.UL_LIST]: (node, children) => <>{children}</>,
  [BLOCKS.LIST_ITEM]: (node, children) => <>{children}</>,
  [BLOCKS.HR]: (node, children) => <>{children}</>,
  [BLOCKS.QUOTE]: (node, children) => <>{children}</>,
  [BLOCKS.EMBEDDED_ENTRY]: (node, children) => <>{children}</>,
  [BLOCKS.EMBEDDED_ASSET]: (node, children) => <>{children}</>,
};

export function RichTextLinksList({ children, className = null, linkIcons = undefined }) {
  const options = useMemo(
    () => ({
      renderText: () => '',
      renderNode: {
        ...escapeList,
        [BLOCKS.PARAGRAPH]: (node, children) => <>{children}</>,
        [INLINES.ENTRY_HYPERLINK]: node => {
          let el = renderEntryHyperlink(node, linkIcons);
          return el ? <li>{el}</li> : null;
        },
        [INLINES.HYPERLINK]: node => {
          let el = renderHyperlink(node, linkIcons);
          return el ? <li>{el}</li> : null;
        },
        [INLINES.ASSET_HYPERLINK]: node => {
          let el = renderAssetHyperlink(node, linkIcons);
          return el ? <li>{el}</li> : null;
        },
      },
    }),
    [linkIcons]
  );
  try {
    return <ul className={mergeClassNames('pwp', styles.richtext, className)}>{renderRichText(children, options)}</ul>;
  } catch (error) {
    return null;
  }
}

export function RichTextDefinitionsList({ children, className = null }) {
  const options = {
    renderText: () => '',
    renderNode: {
      ...escapeList,
      [BLOCKS.PARAGRAPH]: (node, children) => <>{children}</>,
      [INLINES.ENTRY_HYPERLINK]: node => {
        const [content] = node.content;
        const { target } = node.data;
        switch (target.__typename) {
          case 'ContentfulRichTextDefinition':
            return (
              <li>
                <dfn title={target.description}>{content.value}</dfn>
                {': '}
                <span>{target.description}</span>
              </li>
            );
          default:
            return null;
        }
      },
      [INLINES.HYPERLINK]: () => null,
      [INLINES.ASSET_HYPERLINK]: () => null,
    },
  };

  try {
    return <ul className={mergeClassNames('pwp', styles.richtext, className)}>{renderRichText(children, options)}</ul>;
  } catch (error) {
    return null;
  }
}

export function extractRichTextStrings(cmsRichText) {
  let options = {
    renderText: text => text,
    renderNode: {
      ...escapeList,
      [BLOCKS.PARAGRAPH]: (node, children) =>
        Array.isArray(children) ? children.flat(2).find(node => Boolean(node)) : children,
      [INLINES.ENTRY_HYPERLINK]: extractStringContent,
      [INLINES.HYPERLINK]: extractStringContent,
      [INLINES.ASSET_HYPERLINK]: extractStringContent,
      [BLOCKS.EMBEDDED_ASSET]: () => null,
    },
  };

  let renderedString = renderRichText(cmsRichText, options);
  let [text] = Array.isArray(renderedString) ? renderedString : [renderedString];
  return text.toString();
}

function extractStringContent(node) {
  let [content] = node?.content;
  return content;
}
